import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { getClientEnvironment } from '~/config/environment'
import type { DatadogClientConfig } from '~/datadog/datadog.config'

function isDatadogClientEnabled() {
  return (window as any).ENV?.DATADOG_ENABLED === true
}

export async function initializeDatadogClient(config: DatadogClientConfig) {
  // Only run in browser
  if (typeof window === 'undefined' || !isDatadogClientEnabled()) return

  const environment = getClientEnvironment()

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: environment,
    version: config.version,
    sessionSampleRate: config.sessionSampleRate,
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    telemetryConfigurationSampleRate: config.telemetryConfigurationSampleRate,
    telemetryUsageSampleRate: config.telemetryUsageSampleRate,
    telemetrySampleRate: config.telemetrySampleRate,
    traceSampleRate: config.traceSampleRate,
    profilingSampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [() => true], // for all
    excludedActivityUrls: [/gtm\.js/, /cloudflare/, /analytics/, /amplitude/],
    enableExperimentalFeatures: ['clickmap'],
    plugins: [reactPlugin()],
  })

  datadogLogs.init({
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: environment,
    version: config.version,
    sessionSampleRate: config.sessionSampleRate,
    telemetryUsageSampleRate: config.telemetryUsageSampleRate,
    telemetrySampleRate: config.telemetrySampleRate,
    telemetryConfigurationSampleRate: config.telemetryConfigurationSampleRate,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    forwardReports: ['intervention', 'deprecation', 'csp_violation'],
  })

  datadogRum.startSessionReplayRecording()

  console.log(`Datadog RUM initialized for ${environment}`)
}

export async function setDatadogUser(user: { id: string; email?: string; name?: string }) {
  if (typeof window === 'undefined' || !isDatadogClientEnabled()) return
  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
  })
}

export async function trackDatadogError(error: Error | string, context?: object) {
  if (typeof window === 'undefined') return
  datadogRum.addError(error, context)
  datadogLogs.logger.error(typeof error === 'string' ? error : error.message, context)
}
